import { Table } from 'semantic-ui-react'
import {
  Button,
  GridRowColumn,
  Input,
  Text,
} from '@/componentsV1/BaseComponents'
import {
  dollarsToCents,
  centsToDollars,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import {
  FILING_STATUS_FIELDS,
  type FilingStatusKey,
  type TaxRateBracketInner,
  type TaxRateBrackets,
} from '../../IncomeTaxDetailsHelpers'

interface BracketBandDraft {
  maxDollars: string
  rate: string
}

interface FilingStatusDraft {
  startDollars: string
  bands: BracketBandDraft[]
}

export type TaxRateBracketsDraft = Record<FilingStatusKey, FilingStatusDraft>

const blankBand = (): BracketBandDraft => ({ maxDollars: '', rate: '' })

const buildDraft = (
  bandsFor: (key: FilingStatusKey) => FilingStatusDraft
): TaxRateBracketsDraft => ({
  single: bandsFor('single'),
  headOfHousehold: bandsFor('headOfHousehold'),
  marriedFilingSeparately: bandsFor('marriedFilingSeparately'),
  qualifyingWidow: bandsFor('qualifyingWidow'),
  marriedFilingJointly: bandsFor('marriedFilingJointly'),
})

export const emptyBracketsDraft = (): TaxRateBracketsDraft =>
  buildDraft(() => ({ startDollars: '0', bands: [blankBand()] }))

export const bracketsToDraft = (
  brackets: TaxRateBrackets
): TaxRateBracketsDraft =>
  buildDraft((key) => ({
    startDollars: String(centsToDollars(brackets[key][0]?.minInCents ?? 0)),
    bands: brackets[key].map((band) => ({
      maxDollars: band.maxInCents
        ? String(centsToDollars(band.maxInCents))
        : '',
      rate: String(band.rate),
    })),
  }))

/**
 * Every band after the first starts where the one above it ends, so only its
 * upper bound is entered — that makes the gaps, overlaps and zero-width bands
 * that hand-written bracket JSON kept introducing unrepresentable. The first
 * band's start stays editable because some states exempt income below a
 * threshold rather than taxing from zero (Ohio starts at $26,050).
 */
const lowerBoundInCents = (
  { startDollars, bands }: FilingStatusDraft,
  index: number
): number =>
  index === 0
    ? dollarsToCents(startDollars)
    : dollarsToCents(bands[index - 1].maxDollars)

const bandsToBrackets = (draft: FilingStatusDraft): TaxRateBracketInner[] =>
  draft.bands.map((band, index) => ({
    minInCents: lowerBoundInCents(draft, index),
    maxInCents:
      band.maxDollars === '' ? undefined : dollarsToCents(band.maxDollars),
    rate: Number(band.rate),
  }))

export const draftToBrackets = (
  draft: TaxRateBracketsDraft
): TaxRateBrackets => ({
  single: bandsToBrackets(draft.single),
  headOfHousehold: bandsToBrackets(draft.headOfHousehold),
  marriedFilingSeparately: bandsToBrackets(draft.marriedFilingSeparately),
  qualifyingWidow: bandsToBrackets(draft.qualifyingWidow),
  marriedFilingJointly: bandsToBrackets(draft.marriedFilingJointly),
})

const bandsValid = (draft: FilingStatusDraft): boolean =>
  draft.bands.length > 0 &&
  draft.bands.every((band, index) => {
    const rate = Number(band.rate)
    if (band.rate === '' || Number.isNaN(rate) || rate <= 0 || rate >= 1) {
      return false
    }
    if (band.maxDollars === '') {
      return index === draft.bands.length - 1
    }
    return dollarsToCents(band.maxDollars) > lowerBoundInCents(draft, index)
  })

export const isBracketsDraftValid = (draft: TaxRateBracketsDraft): boolean =>
  FILING_STATUS_FIELDS.every(({ key }) => bandsValid(draft[key]))

interface Props {
  value: TaxRateBracketsDraft
  onChange: (next: TaxRateBracketsDraft) => void
}

export const TaxRateBracketsInput = ({ value, onChange }: Props) => (
  <>
    <GridRowColumn>
      <Text as="h3">Tax Rate Brackets</Text>
      <Text as="bodyXs">
        Enter each band&apos;s upper bound and rate. Bands start where the one
        above them ends, so leave the last band&apos;s upper bound blank for the
        top rate. Set the first band&apos;s starting income above $0 only for a
        state that exempts income below a threshold. Rates are decimals below 1
        — enter 0.0525 for 5.25%.
      </Text>
    </GridRowColumn>
    {FILING_STATUS_FIELDS.map(({ key, label }) => {
      const statusDraft = value[key]
      const { bands } = statusDraft
      const setStatus = (next: Partial<FilingStatusDraft>) =>
        onChange({ ...value, [key]: { ...statusDraft, ...next } })
      const setBands = (next: BracketBandDraft[]) => setStatus({ bands: next })
      const updateBand = (index: number, band: Partial<BracketBandDraft>) =>
        setBands(bands.map((b, i) => (i === index ? { ...b, ...band } : b)))

      return (
        <GridRowColumn key={key}>
          <Text as="h3">{label}</Text>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Income over</Table.HeaderCell>
                <Table.HeaderCell>Up to</Table.HeaderCell>
                <Table.HeaderCell>Rate</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {bands.map((band, index) => (
                <Table.Row key={index}>
                  <Table.Cell collapsing>
                    {index === 0 ? (
                      <Input
                        componentType="currency"
                        value={statusDraft.startDollars}
                        onChange={(startDollars) => setStatus({ startDollars })}
                      />
                    ) : (
                      formatCurrencyFromCents(
                        lowerBoundInCents(statusDraft, index)
                      )
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      componentType="currency"
                      value={band.maxDollars}
                      placeholder={
                        index === bands.length - 1 ? 'No limit' : undefined
                      }
                      onChange={(maxDollars) =>
                        updateBand(index, { maxDollars })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      componentType="number"
                      prefix=""
                      decimalScale={5}
                      value={band.rate}
                      onChange={(rate) => updateBand(index, { rate })}
                    />
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <Button
                      variant="link"
                      disabled={bands.length === 1}
                      onClick={() =>
                        setBands(bands.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Button
            variant="secondary"
            onClick={() => setBands([...bands, blankBand()])}
          >
            Add band
          </Button>
          {key !== 'single' && (
            <Button
              variant="link"
              onClick={() =>
                setStatus({
                  startDollars: value.single.startDollars,
                  bands: value.single.bands.map((band) => ({ ...band })),
                })
              }
            >
              Copy from Single
            </Button>
          )}
        </GridRowColumn>
      )
    })}
  </>
)
